<template>
    <div class="form-table__wrapper">
        <div class="form-table" ref="formTable" :class="theme">

            <!-- table header -->
            <div class="form-table__header" :style="fakeHeaderStyle">

                <div class="form-table__header__tr" ref="formTableHeader">

                    <div
                            v-for="(header, hIndex) in headers"
                            :key="'header.' + (header.key || hIndex)"
                            class="form-table__header__th"
                            :style="header.styleInner">

                        <template v-if="isNotEmpty(header.children)">
                            <div class="form-table__header__th__container">
                                <div class="form-table__header__th__row1" :class="header.headerClass">
                                    <slot :name="'header.' + header.key" v-bind="{ header }">

                                        <!-- th content -->
                                        {{ header.text }}

                                    </slot>
                                </div>

                                <div class="form-table__header__th__row2">
                                    <div
                                            v-for="(child, cIndex) in header.children"
                                            :key="'header.' + (child.key || (hIndex + '-' + cIndex))"
                                            :style="child.styleInner"
                                            :class="child.headerClass"
                                            class="form-table__header__th__col"
                                    >

                                        <slot :name="'header.' + child.key" v-bind="{ header: child }">

                                            <!-- th content -->
                                            {{ child.text }}

                                        </slot>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="form-table__header__th__wrapper" :class="header.headerClass">

                                <slot :name="'header.' + header.key + '.wrapper'">

                                    <slot :name="'header.' + header.key + '.prepend'"></slot>

                                    <!-- header th td slot -->
                                    <slot :name="'header.' + header.key" v-bind="{ header }">

                                        <!-- th content -->
                                        {{ header.text }}

                                    </slot>

                                    <slot :name="'header.' + header.key + '.append'"></slot>

                                </slot>

                            </div>
                        </template>

                    </div>

                </div>


                <div
                        v-if="hasFixedItem"
                        class="form-table__fixed-item" :style="{top: headerHeight}"
                        ref="fixedItem"
                >
                    <div class="form-table__fixed-item__slot-wrapper"
                         :style="{width: totalWidth + 'px'}"
                         ref="fixedItemSlotWrapper"
                    >
                        <slot
                                name="fixed-item"
                                v-bind="{ headers, items }"></slot>
                    </div>
                </div>

            </div>

            <!-- table body -->
            <div class="form-table__body" ref="formTableBody">

                <!-- 초기 로딩이 끝날 때까지 로딩 바를 띄웁니다 -->
                <template v-if="loading">
                    <div class="form-table__loading__container">
                        <v-progress-circular
                                :size="50"
                                color="primary"
                                indeterminate
                        ></v-progress-circular>
                    </div>
                </template>

                <!-- 초기 로딩이 완료되면 렌더링을 시작합니다 -->
                <template v-else>

                    <template v-for="(item, index) in items">
                        <div
                                v-show="item.visible"
                                :key="'item.' + item.key"
                                class="form-table__body__tr tabable"
                                tabindex="0"
                                @click="$emit('click:row', item.raw)"
                                @keydown="e => $emit('keydown:tr', e)"
                        >

                            <slot name="item" v-bind="{ item: item.raw, headers, index }">

                                <template v-for="(header, hIndex) in headers">

                                    <template v-if="isNotEmpty(header.children)">

                                        <div
                                                v-for="(child, cIndex) in header.children"
                                                :key="'item.' + item.key + '.' + child.key"
                                                class="form-table__body__td"
                                                :class="child.itemClass && child.itemClass(byString(item.raw, child.key), item.raw)"
                                                :style="child.styleInner">

                                            <slot :name="'item.' + child.key + '.wrapper'"
                                                  v-bind="{ item: item.raw, header: child, index }">

                                                <div class="form-table__body__td__wrapper"
                                                     :class="child.styleWrapper"
                                                >

                                                    <slot :name="'item.' + child.key + '.prepend'"
                                                          v-bind="{ item: item, header: child, index }"></slot>

                                                    <slot :name="'item.' + child.key"
                                                          v-bind="{ item: item, header: child, index }">

                                                        <!-- td content -->
                                                        {{ byString(item, child.key) }}

                                                    </slot>

                                                    <slot :name="'item.' + child.key + '.append'"
                                                          v-bind="{ item: item, header: child, index }"></slot>

                                                </div>

                                            </slot>

                                        </div>

                                    </template>

                                    <template v-else>

                                        <div
                                                :key="'item.' + item.key + '.' + header.key"
                                                class="form-table__body__td"
                                                :class="header.itemClass && header.itemClass(byString(item.raw, header.key), item.raw)"
                                                :style="header.styleInner">

                                            <slot :name="'item.' + header.key + '.wrapper'"
                                                  v-bind="{ item: item.raw, header, index }">

                                                <div class="form-table__body__td__wrapper"
                                                     :class="header.styleWrapper"
                                                >

                                                    <slot :name="'item.' + header.key + '.prepend'"
                                                          v-bind="{ item: item, header, index }"></slot>

                                                    <slot :name="'item.' + header.key"
                                                          v-bind="{ item: item, header, index }">

                                                        <!-- td content -->
                                                        {{ byString(item, header.key) }}

                                                    </slot>

                                                    <slot :name="'item.' + header.key + '.append'"
                                                          v-bind="{ item: item, header, index }"></slot>

                                                </div>

                                            </slot>

                                        </div>

                                    </template>

                                </template>

                            </slot>

                        </div>

                        <!-- 확장 slot -->
                        <div
                                v-if="showExpand"
                                v-show="item.expanded"
                                :key="'item.' + item.key + '.expanded'"
                                class="form-table__body__tr__expanded"
                                style="min-width: 100%;"
                                :style="{
                                width: totalWidth + 'px',
                            }">
                            <slot name="expanded-item" v-bind="{ headers, item, index }"></slot>
                        </div>
                    </template>

                </template>

            </div>

            <slot name="body.append">
            </slot>

        </div>

    </div>
</template>

<script>
    import BaseTableLayoutProps from "@/assets/plugins/mps-form-table/component/BaseTableLayoutProps";
    import tableUtilManager from "@/assets/plugins/mps-form-table/plugins/common/util/table-util-manager";

    const ALIGN = {
        left: {
            justifyContent: "flex-start",
        },
        center: {
            justifyContent: "center",
        },
        right: {
            justifyContent: "flex-end",
        },
    }

    export default {
        name: "BaseTableLayout",
        mixins: [BaseTableLayoutProps],
        props: {
            showExpand: {
                type: Boolean,
                default: false,
            }
        },
        data() {
            return {
                headerHeight: "58px",
                fixedHeight: undefined,
            }
        },
        computed: {
            fakeHeaderStyle() {
                return {
                    minHeight: this.headerHeight,
                    marginBottom: this.fixedHeight,
                }
            },

            totalWidth() {
                let width = 0;
                this.headers.forEach(e => width += (e.width || 0));
                return width;
            },

            hasFixedItem() {
                for (let name in this.$scopedSlots) {
                    if (name === "fixed-item") return true;
                }
                return false;
            },
        },
        watch: {
            headers() {
                this.headers.forEach(e => {
                    e.styleInner = {
                        width: e.width + "px",
                        minWidth: e.width + "px",
                        maxWidth: e.grow === 0 ? e.width + "px" : undefined,
                        ...ALIGN[e.align],
                        flexGrow: e.grow,
                    };
                    e.style = {
                        ...e.styleInner,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        ...ALIGN[e.align],
                        padding: ".6rem .6rem",
                    };
                    e.styleWrapper = {
                        ...ALIGN[e.align],
                    };

                    if (!tableUtilManager.isEmpty(e.children)) {
                        e.children.forEach(child => {
                            child.styleInner = {
                                width: (e.width / e.children.length) + "px",
                                minWidth: (e.width / e.children.length) + "px",
                                maxWidth: (e.width / e.children.length) + "px",
                                ...ALIGN[e.align],
                                flexGrow: 0,
                            };
                            child.style = {
                                ...child.styleInner,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                ...ALIGN[child.align],
                                padding: ".6rem .6rem",
                            };
                            child.styleWrapper = {
                                ...ALIGN[e.align],
                            };
                        });
                    }
                });
            },
        },
        mounted() {
            // 헤더는 absolute 이기 때문에 실질적인 height 를 가지지 않아 테이블 바디 부분과 겹치는 현상이 발생합니다.
            // 스크립트로 해당 현상을 해결합니다.
            this.$nextTick(() => {
                this.resizeHeader();
                this.resizeFixedItem();
            });
            window.onresize = (e => {
                this.resizeHeader();
                this.resizeFixedItem();
                // this.$refs.formTable.scrollTo(0
                //     , this.$refs.formTable.scrollTop);
            });
            // 헤더는 absolute 로 고정되어 있기에 가로 스크롤시에도 고정이 되는 현상이 발생합니다.
            // 스크립트로 해당 현상을 해결합니다.
            this.$refs.formTable.addEventListener('scroll', e => {
                this.$refs.formTableHeader.scrollTo(e.currentTarget.scrollLeft, 0);
                if (this.$refs.fixedItem) this.$refs.fixedItem.scrollTo(e.currentTarget.scrollLeft, 0);
                this.resizeFixedItem();
            });
        },
        methods: {
            /**
             * 헤더 높이 조정
             */
            resizeHeader() {
                this.headerHeight = this.$refs.formTableHeader.clientHeight + "px";
            },

            resizeFixedItem() {
                if (this.$refs.fixedItem) this.fixedHeight = this.$refs.fixedItem.clientHeight + "px";
            },

            isNotEmpty(arr) {
                return !tableUtilManager.isEmpty(arr);
            },

            byString(o, s) {
                return tableUtilManager.byString(o, s);
            },

            assign(o, s, v) {
                tableUtilManager.assign(o, s, v);
            }
        }
    }
</script>

<style scoped>
    /** SCROLLBAR */
    ::-webkit-scrollbar {
        width: 4px;
        z-index: 9999;
        height: 8px;
    }

    ::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    ::-webkit-scrollbar-thumb {
        background: #AAAAAA;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: #888888;
    }

    /** SCROLLBAR END */

    .form-table__wrapper {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .form-table {
        display: flex;
        flex-direction: column;
        justify-content: stretch;
        width: 100%;
        height: 100%;
        text-align: left;
        overflow-y: scroll;
        overflow-x: auto;
        font-size: medium;
    }

    .form-table__header {
        flex-grow: 0;
        border-bottom: 1px solid #ddd;
    }

    .form-table__header .form-table__header__tr {
        display: flex;
        width: calc(100% - 4px);
        flex-direction: row;
        justify-content: start;
        position: absolute;
        top: 0;
        overflow-x: hidden;

    }

    .form-table__header .form-table__header__tr .form-table__header__th {
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .form-table__header .form-table__header__tr .form-table__header__th .form-table__header__th__container {
        display: flex;
        flex-direction: column;
        justify-content: stretch;
    }

    .form-table__header .form-table__header__tr .form-table__header__th .form-table__header__th__container .form-table__header__th__row1 {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .form-table__header .form-table__header__tr .form-table__header__th .form-table__header__th__container .form-table__header__th__row2 {
        display: flex;
        flex-direction: row;
        justify-content: start;
    }

    .form-table__header .form-table__header__tr .form-table__header__th .form-table__header__th__container .form-table__header__th__row2 .form-table__header__th__col {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .form-table__header .form-table__header__tr .form-table__header__th .form-table__header__th__wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    .form-table__fixed-item {
        position: absolute;
        left: 0;
        width: calc(100% - 4px);
        overflow-x: hidden;
        overflow-y: hidden;
        background-color: white;
        z-index: 1;
        border-bottom: 1px solid #ddd;
    }

    .form-table__fixed-item__slot-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: start;
        align-items: center;
        overflow-x: hidden;
        overflow-y: hidden;
        background-color: white;
        z-index: 1;
        border-bottom: 1px solid #ddd;
        min-width: 100%;
    }

    .form-table__body {
        flex-grow: 1;
    }

    .form-table__body .form-table__loading__container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    }

    .form-table__body .form-table__body__tr {
        display: flex;
        width: 100%;
        flex-direction: row;
        justify-content: start;
    }

    .form-table__body .form-table__body__tr .form-table__body__td {
        flex-grow: 0;
        display: flex;
        flex-direction: row;
        flex-basis: 100%;
        justify-content: center;
        align-items: center;
    }

    .form-table__body .form-table__body__tr .form-table__body__td .form-table__body__td__wrapper {
        flex-grow: 0;
        display: flex;
        flex-direction: row;
        flex-basis: 100%;
        justify-content: center;
        align-items: center;
        padding: .6rem .6rem;
    }
</style>